import { useRoute } from '@react-navigation/native';
import { useGetMachineById, useGetMachineCountingProfiles } from '@api/modules/machines/hooks';
import { getStateParams, isPhone, navigateTo } from '@helpers';
import { MachineCountSettingsProps } from '@components/settings/v2/structure/machines/machines/machineCountSettings';
import { COUNTING_METHOD } from '@interfaces/machines';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { configureMachine } from '@reducers/machines';
import { ConfigureMachineRequestDto } from '@api/generated/models';
import { COUNT_SETTINGS_SAVING_TIMEOUT } from '@components/settings/v2/structure/machines/machines/machineCountSettings/constants';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

type MachineDetails = NonNullable<ConfigureMachineRequestDto['machine']>;

interface UseManageMachineCountSettingsParams extends Pick<MachineCountSettingsProps, 'C'> {}

export const useManageMachineCountSettings = ({ C }: UseManageMachineCountSettingsParams) => {
  const route = useRoute();
  const dispatch = useAppDispatch();

  const [currentCountingMethod, setCurrentCountingMethod] = useState<COUNTING_METHOD | null>(null);
  const [isSelectAutomatically, setIsSelectAutomatically] = useState<boolean | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const id = getStateParams(route).id;

  const numericId = Number(id);
  const parsedId = id == null || !Number.isFinite(numericId) ? null : numericId;

  const { loading: machineLoading, machine, refetch: refetchMachine } = useGetMachineById({ id: parsedId });
  const { machineCountingProfiles, loading: machineCountingProfilesLoading } = useGetMachineCountingProfiles();

  const countingMethodBlockTitle = isSelectAutomatically ? 'ACTIVE COUNTING METHOD' : 'COUNTING METHOD';

  const backButtonAction = () => {
    navigateTo('Settings', {
      screen: 'Machines',
      params: {
        screen: 'MachineDetails',
        params: { id: machine?.machineId },
      },
    });
  };

  const countingMethodTextAndDescription = ((): { text: string; description: string } => {
    switch (currentCountingMethod) {
      case COUNTING_METHOD.MACHINE_CONTROL_EVENT: {
        return {
          text: 'Machine Part Counter was automatically selected as the best counting method for this machine.',
          description: 'Quantity is counted when the machine control reports program status "Finished".',
        };
      }
      case COUNTING_METHOD.MACHINE_PART_COUNT: {
        return {
          text: 'Machine Part Counter was automatically selected as the best counting method for this machine.',
          description: 'Quantity is calculated from total part counters on the machine.',
        };
      }
      case COUNTING_METHOD.MACHINE_COUNTER: {
        return {
          text: 'Machine Part Counter was automatically selected as the best counting method for this machine.',
          description: 'Quantity is calculated as delta from execution of M02 and M30 codes by the machine control.',
        };
      }
      case COUNTING_METHOD.LOGICAL: {
        return {
          text: 'Logical Part Counter was automatically selected as the best counting method for this machine.',
          description:
            "Count when machine goes from Active to Change. This is Smart Factory's own detection method and serves as a fallback when no machine counter is available.",
        };
      }
      default: {
        return { text: '', description: '' };
      }
    }
  })();

  const machineConnectionMethod = machine?.machineConnection?.connectionMethod;
  const machineCountingMethod = machine?.machineCountingMethod;
  const machineQuantityType = machine?.machineQuantityType;

  const shouldDisplayQuantityType = currentCountingMethod === COUNTING_METHOD.MACHINE_PART_COUNT;

  const defaultCountingMethods = machineCountingProfiles?.find(
    (profile) => profile.connectionMethod === machineConnectionMethod?.code
  )?.countingMethods;
  const parsedCountingMethods =
    defaultCountingMethods?.some((method) => method?.countingMethod === COUNTING_METHOD.LOGICAL) || defaultCountingMethods == null
      ? defaultCountingMethods
      : [...defaultCountingMethods, { countingMethod: COUNTING_METHOD.LOGICAL }];

  const currentCountingMethodRef = useRef(currentCountingMethod);
  currentCountingMethodRef.current = currentCountingMethod;

  const machineQuantityTypeRef = useRef(machineQuantityType);
  machineQuantityTypeRef.current = machineQuantityType;

  const isSelectAutomaticallyRef = useRef(isSelectAutomatically);
  isSelectAutomaticallyRef.current = isSelectAutomatically;

  const debouncedSave = useCallback(
    (overrides: Partial<MachineDetails> = {}) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        dispatch(
          configureMachine({
            machine: {
              machineId: parsedId ?? undefined,
              machineCountingMethod: currentCountingMethodRef.current as MachineDetails['machineCountingMethod'],
              machineQuantityType: machineQuantityTypeRef.current as MachineDetails['machineQuantityType'],
              selectCountMethodAutomatically: isSelectAutomaticallyRef.current,
              ...overrides,
            },
          })
        );
      }, COUNT_SETTINGS_SAVING_TIMEOUT);
    },
    [dispatch, parsedId]
  );

  const onCountingMethodChange = (countingMethod: COUNTING_METHOD) => {
    setCurrentCountingMethod(countingMethod);
    debouncedSave({ machineCountingMethod: countingMethod as MachineDetails['machineCountingMethod'] });
  };

  const onSelectAutomaticallyChange = useCallback(
    (value: boolean) => {
      setIsSelectAutomatically(value);
      debouncedSave({ selectCountMethodAutomatically: value });
    },
    [debouncedSave]
  );

  const selectAutomaticallyConfig = {
    description: 'When set to Automatic, Smart Factory selects the optimal counting method for this machine.',
    elementConfig: {
      item: {
        value: isSelectAutomatically,
        key: 'switchedOn',
        name: 'Select Automatically',
        noArrow: true,
        isLable: true,
        oneLine: true,
        small: false,
        type: 'boolean',
        loading: machineLoading,
        onChange: onSelectAutomaticallyChange,
        testID: TEST_IDS.machineCountSettings.selectAutomaticallyToggle,
      },
      index: 0,
      array: [{}],
      C,
      topBorder: false,
      bottomBorder: false,
      roundTop: true,
      roundBottom: true,
      threeColumns: !isPhone,
    },
  };

  useEffect(() => {
    if (machine?.selectCountMethodAutomatically != null) {
      setIsSelectAutomatically(!!machine.selectCountMethodAutomatically);
    }
  }, [machine?.selectCountMethodAutomatically]);

  useEffect(() => {
    if (machineCountingMethod) {
      setCurrentCountingMethod(machineCountingMethod as COUNTING_METHOD);
    }
  }, [machineCountingMethod]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    machine,
    countingMethods: parsedCountingMethods,
    currentCountingMethod,
    onCountingMethodChange,
    machineLoading,
    machineCountingProfilesLoading,
    refetchMachine,
    selectAutomaticallyConfig,
    backButtonAction,
    machineConnectionMethod,
    countingMethodBlockTitle,
    isSelectAutomatically,
    countingMethodTextAndDescription,
    machineQuantityType,
    shouldDisplayQuantityType,
  };
};
